import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import type { CustomField as CustomFieldType } from '../../shared/custom_fields/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import CustomField from '../../shared/form/CustomField';
import { BirchDatePickerFormat, RejectionType } from '../../shared/types';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import LinkifiedTooltip from 'candidate_profile_redesign/shared/linkified_tooltip';
import styled from 'styled-components';

interface CustomFieldsFormFieldsProps {
  rejectionReasonType: RejectionType | null;
  customFields: CustomFieldType[];
  currencyOptions: Item[];
  stageTypes: string[];
  userOptionsPath: string;
  dateFormatBirchPicker: BirchDatePickerFormat;
}

const SmallGap = styled.span`
  display: flex;
  gap: 4px;
`;

const SpanWithFlex = styled.span<{ flex: number }>`
  flex: ${({ flex }) => flex};
`;

const CustomFieldsFormFields = ({
  rejectionReasonType,
  customFields,
  currencyOptions,
  stageTypes,
  dateFormatBirchPicker,
  userOptionsPath,
}: CustomFieldsFormFieldsProps) => {
  const hasCurrentValue = (field: CustomFieldType) => {
    return Array.isArray(field.value) ? !!field.value.length : !!field.value;
  };

  const showForStage = (field: CustomFieldType) => {
    if (!rejectionReasonType || rejectionReasonType === 'NONE SPECIFIED') {
      return false;
    }

    const reason =
      rejectionReasonType === 'WE REJECTED THEM'
        ? 'we_rejected_them'
        : 'they_rejected_us';

    const inStage = stageTypes?.includes('offer')
      ? 'in_offer'
      : 'in_another_stage';

    return field.rejection_field_configuration?.[reason]?.[inStage] === true;
  };

  // show field if it matches the current rejection reason type OR it
  // loaded with an existing value
  const showField = (field: CustomFieldType) => {
    return (
      (field.existing_value_id && hasCurrentValue(field)) || showForStage(field)
    );
  };

  const { setValue } = useFormContext();

  if (customFields.length === 0) {
    return null;
  }

  return (
    <Stack>
      {customFields.map((field, index) => {
        const fieldValuesAttributePrefix = field.existing_value_id
          ? `custom-field-${index}-${field.id}-${field.existing_value_id}`
          : `custom-field-${index}-${field.id}`;

        if (!showField(field)) {
          setValue(`${fieldValuesAttributePrefix}-value`, field.value);
          setValue(`${fieldValuesAttributePrefix}-unit`, field.unit);

          return null;
        }

        return (
          <SmallGap key={field.id}>
            <SpanWithFlex flex={1}>
              <CustomField
                field={field}
                currencyOptions={currencyOptions}
                showFieldNameLabel={true}
                index={index}
                dateFormatBirchPicker={dateFormatBirchPicker}
                userOptionsPath={userOptionsPath}
              />
            </SpanWithFlex>
            {field.description && (
              <SpanWithFlex flex={0}>
                <LinkifiedTooltip
                  description={field.description}
                  data-provides={'custom-field-description'}
                />
              </SpanWithFlex>
            )}
          </SmallGap>
        );
      })}
    </Stack>
  );
};

export default CustomFieldsFormFields;
